import { IDocument } from "@faire/web-api/indigofair/data/IDocument";
import { IImage } from "@faire/web-api/indigofair/data/IImage";
import { WebApiError } from "@faire/web-api/WebApiError";
import { uploadImageFile } from "@faire/web/common/images/uploadImageFile";
import { logError } from "@faire/web/common/logging";
import { isNotNull } from "@faire/web/common/typescriptUtils";
import { useState, useRef } from "react";
import { FileRejection } from "react-dropzone";

import { FileRejectionCode } from "./const";
import { IDroppedFile } from "./DroppedFile";
import { uploadFile } from "./uploadFile";

interface IUseFileUploader {
  setDocuments: (files: IDocument[]) => void;
  onUploadImage?: (image: IImage) => void;
  onUploadSuccess?: (fileState: IDroppedFile) => void;
  onUploadFailure?: (fileState: IDroppedFile, errorMessage?: string) => void;
}

export const useFileUploader = ({
  setDocuments,
  onUploadImage,
  onUploadSuccess,
  onUploadFailure,
}: IUseFileUploader) => {
  // A ref is used because multiple files can be dropped at the same time
  const uploadedFilesRef = useRef<IDocument[]>([]);
  // setDocuments triggers state changes

  // Uploading Files
  const [uploading, setUploadingBase] = useState<{
    [key: string]: IDroppedFile;
  }>({});
  const setUploading = (key: string, fileState: IDroppedFile) =>
    setUploadingBase((prevState) => ({ ...prevState, [key]: fileState }));

  // Preview Images
  const [imageState, setImageStateBase] = useState<{ [key: string]: string }>(
    {}
  );
  const setImageState = (key?: string, imageUrl?: string) => {
    if (key === undefined || imageUrl === undefined) {
      return;
    }
    setImageStateBase((prevState) => ({ ...prevState, [key]: imageUrl }));
  };

  const removeFromUploading = (key: string) => {
    const { [key]: _, ...rest } = uploading;
    setUploadingBase(rest);
  };

  const [dragOver, setDragOver] = useState(false);

  const handleFileUpload = async (file: File, previewImgUrl?: string) => {
    const fileState: IDroppedFile = {
      fileName: file.name,
      fileSize: file.size,
    };
    try {
      const uploadedFile = await uploadFile(file);

      // Update State
      setImageState(uploadedFile.file_name, previewImgUrl);
      removeFromUploading(file.name);

      // Update Uploaded Files State
      const existingFileIndex = uploadedFilesRef.current.findIndex(
        (doc) =>
          extractName(doc.file_name) === extractName(uploadedFile.file_name)
      );
      if (existingFileIndex !== -1) {
        uploadedFilesRef.current = uploadedFilesRef.current.map((doc, index) =>
          index === existingFileIndex ? uploadedFile : doc
        );
        setDocuments(uploadedFilesRef.current);
      }
      uploadedFilesRef.current = [...uploadedFilesRef.current, uploadedFile];
      setDocuments(uploadedFilesRef.current);
      onUploadSuccess?.(fileState);
    } catch (e) {
      const errorMessage =
        e instanceof WebApiError ? e.responseData.localized_message : undefined;
      fileState.isUploading = false;
      fileState.errors = [FileRejectionCode.NETWORK_ERROR];
      setUploading(file.name, fileState);
      onUploadFailure?.(fileState, errorMessage);
    }
  };

  const onDropAccepted = async (files: File[]) => {
    setDragOver(false);
    files.forEach(async (file) => {
      let previewImage = IImage.build({});
      if (file.type.startsWith("image")) {
        try {
          previewImage = await uploadImageFile(file);
          onUploadImage?.(previewImage);
        } catch (e) {
          logError(e);
        }
      }

      const droppedFile: IDroppedFile = {
        fileName: file.name,
        fileSize: file.size,
        isUploading: true,
        imgPreviewUrl: previewImage.url,
      };
      setUploading(file.name, droppedFile);

      handleFileUpload(file, previewImage.url);
    });
  };

  const onDropRejected = async (fileRejections: FileRejection[]) => {
    setDragOver(false);
    fileRejections.forEach((fileRejection) => {
      const { file, errors } = fileRejection;
      const droppedFile: IDroppedFile = {
        fileName: file.name,
        isUploading: false,
        errors: errors.map(({ code }) => code),
      };

      setUploading(file.name, droppedFile);
    });
  };

  const onRemove = (fileName?: string) => {
    if (!fileName) {
      return;
    }
    removeFromUploading(fileName);
    uploadedFilesRef.current = uploadedFilesRef.current.filter(
      (doc) => extractName(doc.file_name) !== extractName(fileName)
    );
    setDocuments(uploadedFilesRef.current);
  };

  const getPreviewImage = (fileName?: string) => {
    if (!fileName) {
      return undefined;
    }
    if (fileName in imageState) {
      return imageState[fileName];
    }
    return undefined;
  };

  return {
    uploading,
    onDropAccepted,
    onDropRejected,
    onRemove,
    dragOver,
    setDragOver,
    getPreviewImage,
  };
};

// eg. "d_khktjyhc74-Image9.png" -> "Image9.png"
export const extractName = (fileName?: string) => {
  if (!fileName) {
    return undefined;
  }
  const match = (fileName ?? "").match(/d_[a-z0-9]+-(.+)/i);
  if (isNotNull(match) && match.length > 1) {
    return match[1];
  }
  return fileName;
};
